import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { expenseApi } from "../../core/api/expenseApi";

const CATEGORIES = [
  "DOMAIN",
  "VPS",
  "SOFTWARE",
  "SALARY",
  "HARDWARE",
  "MARKETING",
  "TRAVEL",
  "OTHER",
];

const PAYMENT_MODES = ["CASH", "UPI", "CARD", "BANK", "WALLET", "OTHER"];
const STATUSES = ["PAID", "PENDING", "CANCELLED"];

const emptyForm = {
  title: "",
  amount: "",
  expense_date: "",
  validity_end: "",
  vendor: "",
  category: "OTHER",
  payment_mode: "OTHER",
  status: "PAID",
  notes: "",
  receipt: null,

  // future-proof
  is_recurring: false,
  billing_cycle: "ONE_TIME",
  next_renewal_date: "",
  reminder_days_before: 7,

  branch: "",
  department: "",
};

function Modal({ open, title, onClose, children, footer }) {
  useEffect(() => {
    if (!open) return;

    // lock body scroll + prevent layout shift when scrollbar disappears
    const scrollBarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;

    document.body.style.overflow = "hidden";
    if (scrollBarWidth > 0) document.body.style.paddingRight = `${scrollBarWidth}px`;

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [open]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[99999] bg-black/60">
      {/* overlay click closes */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* modal wrapper */}
      <div className="relative z-[100000] flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b p-4">
            <h3 className="text-lg font-semibold">{title}</h3>
            <button
              onClick={onClose}
              className="rounded-md px-3 py-1 text-sm hover:bg-gray-100"
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div className="p-4 max-h-[70vh] overflow-y-auto">{children}</div>

          {/* Footer */}
          {footer ? <div className="border-t p-4">{footer}</div> : null}
        </div>
      </div>
    </div>,
    document.body
  );
}

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      {Array.from({ length: 9 }).map((_, i) => (
        <td key={i} className="p-3">
          <div className="h-4 w-full rounded bg-gray-200" />
        </td>
      ))}
    </tr>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="mt-1 text-sm font-medium text-gray-900">{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="mb-1 text-xs text-gray-600">{label}</div>
      {children}
    </div>
  );
}

function ExpenseForm({ form, setForm }) {
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <Field label="Expense Name">
        <input
          className="w-full rounded-lg border p-2 text-sm"
          value={form.title}
          onChange={(e) => set("title", e.target.value)}
        />
      </Field>

      <Field label="Amount (₹)">
        <input
          className="w-full rounded-lg border p-2 text-sm"
          type="number"
          value={form.amount}
          onChange={(e) => set("amount", e.target.value)}
        />
      </Field>

      <Field label="Expense Date">
        <input
          className="w-full rounded-lg border p-2 text-sm"
          type="date"
          value={form.expense_date}
          onChange={(e) => set("expense_date", e.target.value)}
        />
      </Field>

      <Field label="Validity / Expiry (optional)">
        <input
          className="w-full rounded-lg border p-2 text-sm"
          type="date"
          value={form.validity_end || ""}
          onChange={(e) => set("validity_end", e.target.value)}
        />
      </Field>

      <Field label="Vendor (optional)">
        <input
          className="w-full rounded-lg border p-2 text-sm"
          value={form.vendor}
          onChange={(e) => set("vendor", e.target.value)}
        />
      </Field>

      <Field label="Category">
        <select
          className="w-full rounded-lg border p-2 text-sm"
          value={form.category}
          onChange={(e) => set("category", e.target.value)}
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Payment Mode">
        <select
          className="w-full rounded-lg border p-2 text-sm"
          value={form.payment_mode}
          onChange={(e) => set("payment_mode", e.target.value)}
        >
          {PAYMENT_MODES.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Status">
        <select
          className="w-full rounded-lg border p-2 text-sm"
          value={form.status}
          onChange={(e) => set("status", e.target.value)}
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Branch (optional)">
        <input
          className="w-full rounded-lg border p-2 text-sm"
          value={form.branch}
          onChange={(e) => set("branch", e.target.value)}
        />
      </Field>

      <Field label="Department (optional)">
        <input
          className="w-full rounded-lg border p-2 text-sm"
          value={form.department}
          onChange={(e) => set("department", e.target.value)}
        />
      </Field>

      <div className="md:col-span-2 rounded-lg border p-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!form.is_recurring}
            onChange={(e) => set("is_recurring", e.target.checked)}
          />
          Recurring Expense (domain/vps/subscription)
        </label>

        {form.is_recurring ? (
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
            <Field label="Billing Cycle">
              <select
                className="w-full rounded-lg border p-2 text-sm"
                value={form.billing_cycle}
                onChange={(e) => set("billing_cycle", e.target.value)}
              >
                {["ONE_TIME", "MONTHLY", "QUARTERLY", "YEARLY"].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Next Renewal Date">
              <input
                className="w-full rounded-lg border p-2 text-sm"
                type="date"
                value={form.next_renewal_date || ""}
                onChange={(e) => set("next_renewal_date", e.target.value)}
              />
            </Field>

            <Field label="Reminder days before">
              <input
                className="w-full rounded-lg border p-2 text-sm"
                type="number"
                value={form.reminder_days_before}
                onChange={(e) => set("reminder_days_before", e.target.value)}
              />
            </Field>
          </div>
        ) : null}
      </div>

      <div className="md:col-span-2">
        <Field label="Notes (optional)">
          <textarea
            className="w-full rounded-lg border p-2 text-sm"
            rows={3}
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
          />
        </Field>
      </div>

      <div className="md:col-span-2">
        <Field label="Receipt (optional image/pdf)">
          <input
            className="w-full rounded-lg border p-2 text-sm"
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => set("receipt", e.target.files?.[0] || null)}
          />
          <p className="mt-1 text-xs text-gray-500">
            If you upload a receipt, API uses multipart/form-data.
          </p>
        </Field>
      </div>
    </div>
  );
}

export default function AdminExpenses() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [totalSummary, setTotalSummary] = useState({ total: "0.00", count: 0 });

  // pagination
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // filters
  const [filters, setFilters] = useState({
    search: "",
    from_date: "",
    to_date: "",
    category: "",
    payment_mode: "",
    status: "",
    min_amount: "",
    max_amount: "",
    ordering: "-expense_date",
  });

  // modals
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  const [active, setActive] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const totalPages = useMemo(
    () => Math.ceil((count || 0) / pageSize),
    [count]
  );

  const cleanParams = (params) => {
    const p = { ...params };
    Object.keys(p).forEach((k) => {
      if (p[k] === "" || p[k] === null || p[k] === undefined) delete p[k];
    });
    return p;
  };

  const fetchTotalSummary = async () => {
    try {
      const params = cleanParams({ ...filters }); // page/page_size mat bhejo
      const data = await expenseApi.totalSummary(params);

      setTotalSummary({
        total: data?.total ?? "0.00",
        count: data?.count ?? 0,
      });
    } catch (e) {
      console.error(e);
      setTotalSummary({ total: "0.00", count: 0 });
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = cleanParams({
        ...filters,
        page,
        page_size: pageSize,
      });

      const data = await expenseApi.list(params);
      await fetchTotalSummary();

      // DRF pagination: {count, results}
      if (data && Array.isArray(data.results)) {
        setRows(data.results);
        setCount(data.count || 0);
      } else if (Array.isArray(data)) {
        setRows(data);
        setCount(data.length);
      } else {
        setRows([]);
        setCount(0);
      }
    } catch (e) {
      console.error(e);
      setRows([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [page]);

  const applyFilters = () => {
    setPage(1);
    setTimeout(() => fetchData(), 0);
  };

  const resetFilters = () => {
    setFilters({
      search: "",
      from_date: "",
      to_date: "",
      category: "",
      payment_mode: "",
      status: "",
      min_amount: "",
      max_amount: "",
      ordering: "-expense_date",
    });
    setPage(1);
    setTimeout(fetchData, 0);
  };

  const openAddModal = () => {
    setForm(emptyForm);
    setOpenAdd(true);
  };

  const openEditModal = (row) => {
    setActive(row);
    setForm({
      ...emptyForm,
      ...row,
      amount: row.amount ?? "",
      receipt: null, // do not prefill file
    });
    setOpenEdit(true);
  };

  const openViewModal = (row) => {
    setActive(row);
    setOpenView(true);
  };

  const openDeleteModal = (row) => {
    setActive(row);
    setOpenDelete(true);
  };

  const buildFormData = () => {
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") return;

      if (k === "receipt") {
        if (v) fd.append("receipt", v);
        return;
      }

      fd.append(k, v);
    });
    return fd;
  };

  const createExpense = async () => {
    try {
      const fd = buildFormData();
      await expenseApi.create(fd);
      setOpenAdd(false);
      fetchData();
    } catch (e) {
      console.error(e);
      alert("Create failed. Check console.");
    }
  };

  const updateExpense = async () => {
    try {
      const hasFile = !!form.receipt;
      if (hasFile) {
        const fd = buildFormData();
        await expenseApi.update(active.id, fd, true);
      } else {
        const payload = { ...form };
        delete payload.receipt;
        await expenseApi.update(active.id, payload, false);
      }
      setOpenEdit(false);
      fetchData();
    } catch (e) {
      console.error(e);
      alert("Update failed. Check console.");
    }
  };

  const deleteExpense = async () => {
    try {
      await expenseApi.remove(active.id);
      setOpenDelete(false);
      fetchData();
    } catch (e) {
      console.error(e);
      alert("Delete failed. Check console.");
    }
  };

  const exportCsv = async () => {
    try {
      const params = cleanParams({ ...filters });
      const res = await expenseApi.exportCsv(params);

      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "expenses.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert("CSV export failed.");
    }
  };

  return (
    <div className="p-6">
      {/* ✅ Responsive Header (Total visible on mobile too) */}
      <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Expenses</h1>
          <p className="text-sm text-gray-600">
            Manage company expenses (CRUD + receipt + filters)
          </p>
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-end">
          {/* ✅ Total Expenses Card */}
          <div className="w-full md:w-auto rounded-xl border bg-white px-5 py-3 shadow-sm">
            <div className="text-[11px] text-gray-500">Total Expenses (Filtered)</div>
            <div className="text-lg font-bold">
              ₹ {Number(totalSummary.total || 0).toFixed(2)}
            </div>
            <div className="text-[11px] text-gray-500">
              Records: {totalSummary.count}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-wrap gap-2 md:flex-nowrap md:justify-end">
            <button
              onClick={exportCsv}
              className="flex-1 md:flex-none rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
            >
              Export CSV
            </button>
            <button
              onClick={openAddModal}
              className="flex-1 md:flex-none rounded-lg bg-black px-4 py-2 text-sm text-white hover:bg-gray-800"
            >
              + Add Expense
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 rounded-xl border bg-white p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <input
            className="rounded-lg border p-2 text-sm"
            placeholder="Search (title/vendor/notes/category/payment/created_by)"
            value={filters.search}
            onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
          />

          <select
            className="rounded-lg border p-2 text-sm"
            value={filters.category}
            onChange={(e) => setFilters((p) => ({ ...p, category: e.target.value }))}
          >
            <option value="">All Categories</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <select
            className="rounded-lg border p-2 text-sm"
            value={filters.payment_mode}
            onChange={(e) => setFilters((p) => ({ ...p, payment_mode: e.target.value }))}
          >
            <option value="">All Payment Modes</option>
            {PAYMENT_MODES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <select
            className="rounded-lg border p-2 text-sm"
            value={filters.status}
            onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
          >
            <option value="">All Status</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <input
            className="rounded-lg border p-2 text-sm"
            type="date"
            value={filters.from_date}
            onChange={(e) => setFilters((p) => ({ ...p, from_date: e.target.value }))}
          />

          <input
            className="rounded-lg border p-2 text-sm"
            type="date"
            value={filters.to_date}
            onChange={(e) => setFilters((p) => ({ ...p, to_date: e.target.value }))}
          />

          <input
            className="rounded-lg border p-2 text-sm"
            placeholder="Min amount"
            type="number"
            value={filters.min_amount}
            onChange={(e) => setFilters((p) => ({ ...p, min_amount: e.target.value }))}
          />

          <input
            className="rounded-lg border p-2 text-sm"
            placeholder="Max amount"
            type="number"
            value={filters.max_amount}
            onChange={(e) => setFilters((p) => ({ ...p, max_amount: e.target.value }))}
          />
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            onClick={applyFilters}
            className="rounded-lg bg-black px-4 py-2 text-sm text-white hover:bg-gray-800"
          >
            Apply
          </button>
          <button
            onClick={resetFilters}
            className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-600">
              <tr>
                <th className="p-3">Title</th>
                <th className="p-3">Amount</th>
                <th className="p-3">Date</th>
                <th className="p-3">Validity</th>
                <th className="p-3">Category</th>
                <th className="p-3">Mode</th>
                <th className="p-3">Status</th>
                <th className="p-3">Receipt</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <>
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                </>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="p-6 text-center text-gray-500">
                    No expenses found.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="p-3 font-medium">{r.title}</td>
                    <td className="p-3">₹ {r.amount}</td>
                    <td className="p-3">{r.expense_date}</td>
                    <td className="p-3">{r.validity_end || "-"}</td>
                    <td className="p-3">{r.category}</td>
                    <td className="p-3">{r.payment_mode}</td>
                    <td className="p-3">
                      <span className="rounded-full border px-2 py-1 text-xs">
                        {r.status}
                      </span>
                    </td>
                    <td className="p-3">
                      {r.receipt_url ? (
                        <a
                          href={r.receipt_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          Open
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openViewModal(r)}
                          className="rounded-md border px-3 py-1 text-xs hover:bg-gray-50"
                        >
                          View
                        </button>
                        <button
                          onClick={() => openEditModal(r)}
                          className="rounded-md border px-3 py-1 text-xs hover:bg-gray-50"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => openDeleteModal(r)}
                          className="rounded-md border px-3 py-1 text-xs text-red-600 hover:bg-red-50"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between border-t p-3 text-sm">
          <div className="text-gray-600">
            Total: <span className="font-semibold">{count}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-md border px-3 py-1 disabled:opacity-50"
            >
              Prev
            </button>

            <span className="text-gray-700">
              Page <b>{page}</b> / <b>{totalPages || 1}</b>
            </span>

            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-md border px-3 py-1 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Add Modal */}
      <Modal
        open={openAdd}
        title="Add Expense"
        onClose={() => setOpenAdd(false)}
        footer={
          <div className="flex justify-end gap-2">
            <button
              className="rounded-lg border px-4 py-2 text-sm"
              onClick={() => setOpenAdd(false)}
            >
              Cancel
            </button>
            <button
              className="rounded-lg bg-black px-4 py-2 text-sm text-white"
              onClick={createExpense}
            >
              Save
            </button>
          </div>
        }
      >
        <ExpenseForm form={form} setForm={setForm} />
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={openEdit}
        title="Edit Expense"
        onClose={() => setOpenEdit(false)}
        footer={
          <div className="flex justify-end gap-2">
            <button
              className="rounded-lg border px-4 py-2 text-sm"
              onClick={() => setOpenEdit(false)}
            >
              Cancel
            </button>
            <button
              className="rounded-lg bg-black px-4 py-2 text-sm text-white"
              onClick={updateExpense}
            >
              Update
            </button>
          </div>
        }
      >
        <ExpenseForm form={form} setForm={setForm} />
      </Modal>

      {/* View Modal */}
      <Modal
        open={openView}
        title="Expense Detail"
        onClose={() => setOpenView(false)}
        footer={
          <div className="flex justify-end">
            <button
              className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
              onClick={() => setOpenView(false)}
            >
              Close
            </button>
          </div>
        }
      >
        {active ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Info label="Title" value={active.title} />
            <Info label="Amount" value={`₹ ${active.amount}`} />
            <Info label="Expense Date" value={active.expense_date} />
            <Info label="Validity / Expiry" value={active.validity_end || "-"} />
            <Info label="Vendor" value={active.vendor || "-"} />
            <Info label="Category" value={active.category || "-"} />
            <Info label="Payment Mode" value={active.payment_mode || "-"} />
            <Info label="Status" value={active.status || "-"} />
            <Info label="Branch" value={active.branch || "-"} />
            <Info label="Department" value={active.department || "-"} />

            <Info label="Billing Cycle" value={active.billing_cycle || "-"} />
            <Info label="Next Renewal Date" value={active.next_renewal_date || "-"} />
            <Info
              label="Reminder Days Before"
              value={
                active.reminder_days_before !== null &&
                active.reminder_days_before !== undefined
                  ? String(active.reminder_days_before)
                  : "-"
              }
            />

            <Info label="Created By (ID)" value={active.created_by ?? "-"} />
            <Info label="Created By" value={active.created_by_name || "-"} />
            <Info label="Created At" value={active.created_at || "-"} />
            <Info label="Updated At" value={active.updated_at || "-"} />

            <div className="md:col-span-2">
              <Info label="Notes" value={active.notes || "-"} />
            </div>

            <div className="md:col-span-2">
              <Info
                label="Receipt"
                value={
                  active.receipt_url ? (
                    <a
                      className="text-blue-600 hover:underline"
                      href={active.receipt_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open Receipt
                    </a>
                  ) : (
                    "-"
                  )
                }
              />
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Delete Modal */}
      <Modal
        open={openDelete}
        title="Confirm Delete"
        onClose={() => setOpenDelete(false)}
        footer={
          <div className="flex justify-end gap-2">
            <button
              className="rounded-lg border px-4 py-2 text-sm"
              onClick={() => setOpenDelete(false)}
            >
              Cancel
            </button>
            <button
              className="rounded-lg bg-red-600 px-4 py-2 text-sm text-white"
              onClick={deleteExpense}
            >
              Delete
            </button>
          </div>
        }
      >
        <p className="text-sm text-gray-700">
          This will <b>cancel</b> the expense (soft delete). Continue?
        </p>
        {active ? (
          <p className="mt-2 text-sm">
            <b>{active.title}</b> — ₹ {active.amount}
          </p>
        ) : null}
      </Modal>
    </div>
  );
}
